import { apiJsonRequest } from './apiClient'

function unwrapApiSuccessEnvelope(payload: unknown): unknown {
  if (!payload || typeof payload !== 'object' || Array.isArray(payload)) return payload
  const record = payload as Record<string, unknown>
  if ('data' in record) return record.data
  return payload
}

function readStringField(record: Record<string, unknown>, keys: string[]): string {
  for (const key of keys) {
    const value = record[key]
    if (typeof value === 'string' && value.trim()) return value.trim()
  }
  return ''
}

/**
 * First-step response should include a result URL used by the result API.
 * Accept common shapes from gateway envelopes / Azure operation locations.
 */
export function extractDrivingLicenceResultUrl(payload: unknown): string {
  const unwrapped = unwrapApiSuccessEnvelope(payload)
  if (!unwrapped || typeof unwrapped !== 'object' || Array.isArray(unwrapped)) return ''
  const record = unwrapped as Record<string, unknown>
  return readStringField(record, [
    'result_url',
    'resultUrl',
    'operation_location',
    'operationLocation',
    'url',
  ])
}

/**
 * Submit a public driving-licence image URL for Azure analysis.
 * POST /integrations/azure/driving-licence  body: { url }
 */
export async function submitDrivingLicenceAnalyze(fileUrl: string): Promise<unknown> {
  const resultPayload = await apiJsonRequest<unknown>('/integrations/azure/driving-licence', {
    method: 'POST',
    body: JSON.stringify({
      url: fileUrl,
    }),
  })
  console.log('[DrivingLicence] submit response:', resultPayload)
  return unwrapApiSuccessEnvelope(resultPayload)
}

/**
 * Fetch processed driving-licence analysis using the result URL from submit.
 * POST /integrations/azure/driving-licence-result  body: { result_url }
 */
export async function fetchDrivingLicenceResult(resultUrl: string): Promise<unknown> {
  const resultPayload = await apiJsonRequest<unknown>('/integrations/azure/driving-licence-result', {
    method: 'POST',
    body: JSON.stringify({
      result_url: resultUrl,
    }),
  })
  console.log('[DrivingLicence] result response:', resultPayload)
  return unwrapApiSuccessEnvelope(resultPayload)
}

/**
 * Full driving-licence flow: submit image URL, then fetch analysis via result_url.
 * If the submit response already includes analyzeResult/documents, return it directly.
 */
export async function analyzeDrivingLicenceFromUrl(fileUrl: string): Promise<unknown> {
  const submitPayload = await submitDrivingLicenceAnalyze(fileUrl)
  if (looksLikeAnalyzeResult(submitPayload)) {
    return submitPayload
  }
  const resultUrl = extractDrivingLicenceResultUrl(submitPayload)
  if (!resultUrl) {
    throw new Error('Driving licence analysis did not return a result URL.')
  }
  return fetchDrivingLicenceResult(resultUrl)
}

function looksLikeAnalyzeResult(payload: unknown): boolean {
  if (!payload || typeof payload !== 'object' || Array.isArray(payload)) return false
  const record = payload as Record<string, unknown>
  if (record.analyzeResult && typeof record.analyzeResult === 'object') return true
  if (Array.isArray(record.documents)) return true
  return false
}
